import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { Alert, Button, Paper, Stack, Typography } from '@mui/material';
import React, { useRef, useState } from 'react';

/**
 * FileUploadBox — a reusable drag-and-drop / click-to-browse file upload area.
 *
 * Props:
 *   accept        {string}   MIME type(s) passed to <input accept>. Default: 'application/pdf'
 *   acceptLabel   {string}   Human-readable format hint shown in the empty state. Default: 'PDF'
 *   onFileChange  {Function} Called with the selected File object (or null when removed).
 */
function FileUploadBox({ accept = 'application/pdf', acceptLabel = 'PDF', onFileChange }) {
    const [file, setFile] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [multiFileWarning, setMultiFileWarning] = useState(false);
    const [typeError, setTypeError] = useState(false);
    const inputRef = useRef(null);

    const selectFile = (selected) => {
        if (!selected) return;
        setFile(selected);
        onFileChange?.(selected);
    };

    const removeFile = (e) => {
        e.stopPropagation();
        setFile(null);
        onFileChange?.(null);
        // Reset the input so the same file can be re-selected
        if (inputRef.current) inputRef.current.value = '';
    };

    const isAccepted = (f) => {
        if (!accept) return true;
        return accept.split(',').map(t => t.trim()).some(t => f.type === t);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const dropped = e.dataTransfer.files[0];
        if (dropped && !isAccepted(dropped)) {
            setTypeError(true);
            return;
        }
        setMultiFileWarning(e.dataTransfer.files.length > 1);
        setTypeError(false);
        selectFile(dropped);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        if (!isDragging) setIsDragging(true);
    };

    const handleDragLeave = () => setIsDragging(false);

    return (
        <Stack spacing={1}>
            {multiFileWarning && (
                <Alert severity='warning' onClose={() => setMultiFileWarning(false)}>
                    Only one file can be uploaded at a time. The first file has been selected.
                </Alert>
            )}
            {typeError && (
                <Alert severity='error' onClose={() => setTypeError(false)}>
                    Invalid file type. Please upload a {acceptLabel} file.
                </Alert>
            )}
            <Paper
                elevation={0}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => !file && inputRef.current?.click()}
                sx={{
                    border: '2px dashed',
                    borderColor: isDragging ? 'blue.main' : file ? 'green.main' : 'background.disabled',
                    borderRadius: 2,
                    p: 6,
                    textAlign: 'center',
                    cursor: file ? 'default' : 'pointer',
                    backgroundColor: isDragging ? 'background.green' : 'background.white',
                    transition: 'border-color 0.2s, background-color 0.2s',
                }}
            >
                <input
                    ref={inputRef}
                    type='file'
                    accept={accept}
                    style={{ display: 'none' }}
                    onChange={(e) => selectFile(e.target.files[0])}
                />

                {file ? (
                    <Stack spacing={2} alignItems='center'>
                        <InsertDriveFileOutlinedIcon sx={{ fontSize: 48, color: 'green.main' }} />
                        <Typography variant='heavy' sx={{ fontSize: '15px' }}>
                            {file.name}
                        </Typography>
                        <Typography sx={{ fontSize: '13px', color: 'subtext.main' }}>
                            {(file.size / 1024).toFixed(1)} KB
                        </Typography>
                        <Button size='small' variant='text' color='error' onClick={removeFile}>
                        Remove
                        </Button>
                    </Stack>
                ) : (
                    <Stack spacing={2} alignItems='center'>
                        <CloudUploadOutlinedIcon sx={{ fontSize: 48, color: '#9CA3AF' }} />
                        <Typography variant='heavy' sx={{ fontSize: '15px' }}>
                        Drag &amp; drop your file here
                        </Typography>
                        <Typography sx={{ fontSize: '13px', color: 'subtext.main' }}>
                        or click to browse files
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: 'subtext.main' }}>
                        Supported format: {acceptLabel}
                        </Typography>
                    </Stack>
                )}
            </Paper>
        </Stack>
    );
}

export default FileUploadBox;
